import { useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { compareReleasesByRecency } from "../utils/releaseOptions";
import { ideaKeys } from "./ideaPage/ideaKeys";

import {
  CreateRelease,
  CreateReleaseGroup,
  IdeaReleaseAssignment,
  PaginatedResult,
  ProductItem,
  UpdateRelease,
  UpdateReleaseGroup,
  createRelease,
  createReleaseGroup,
  deleteRelease,
  deleteReleaseGroup,
  listReleaseGroups,
  listReleases,
  updateIdeaReleases,
  updateProductItemReleases,
  updateRelease,
  updateReleaseGroup,
} from "../api/releases";
import { getApiErrorMessage } from "../api/error-message";

/**
 * Query keys factory — same shape as `knowledgeDataKeys` in useKnowledgeData.
 * Everything releases-related hangs off `releaseKeys.all`, so a single
 * invalidate covers both collections when they move together.
 */
export const releaseKeys = {
  all: ["releases"] as const,
  releases: () => [...releaseKeys.all, "list"] as const,
  release: (id: string) => [...releaseKeys.all, "detail", id] as const,
  groups: () => [...releaseKeys.all, "groups"] as const,
};


/**
 * Oldest first, which is the order the stored `index` counter used to give. ObjectId is
 * monotonic, so `_id` ascending reproduces it without the counter. The server returns groups
 * in `createdAt` desc, so without this they would render newest-first.
 */
const byCreationOrder = (a: { _id: string }, b: { _id: string }) =>
  String(a._id || "").localeCompare(String(b._id || ""));

/**
 * What the pickers load: the 500 most recent releases, which the server now orders itself
 * (`sortDate` desc, then `_id` desc).
 *
 * Not the server's 1000 cap, on purpose. A picker is a chooser, and a list past a few hundred
 * is browsed by searching rather than scrolling — so past this the answer is the search box,
 * which queries the backend, rather than a longer list nobody reads. `releaseTruncated` says
 * when the cap is actually biting, so the picker can tell the user to search instead of
 * quietly showing a partial list.
 */
const PICKER_PAGE_SIZE = 500;

/** Groups are few and every picker needs all of them to resolve a tag name. */
const GROUPS_PAGE_SIZE = 1000;

export const useReleases = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  const baseQueryConfig = {
    enabled: !!auth0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  };

  const warnIfTruncated = <T>(
    label: string,
    result: PaginatedResult<T>
  ): PaginatedResult<T> => {
    if (result?.hasMore) {
      console.warn(
        `Releases: ${label} came back truncated (${result.items?.length} of ${result.total}).`
      );
    }
    return result;
  };

  const releasesQuery = useQuery({
    ...baseQueryConfig,
    queryKey: releaseKeys.releases(),
    queryFn: () =>
      listReleases(auth0, { pageSize: PICKER_PAGE_SIZE }).then((result) =>
        warnIfTruncated("GET /releases", result)
      ),
  });

  const groupsQuery = useQuery({
    ...baseQueryConfig,
    queryKey: releaseKeys.groups(),
    queryFn: () =>
      listReleaseGroups(auth0, { pageSize: GROUPS_PAGE_SIZE }).then((result) =>
        warnIfTruncated("GET /release-groups", result)
      ),
  });

  /**
   * Re-sorted client-side even though the server already ordered them, so a release created
   * or edited in this session lands in the right place before the query refetches. The two
   * rules are the same one — see `compareReleasesByRecency` and `releases/sorting.ts`.
   */
  const releases = useMemo(
    () => [...(releasesQuery.data?.items || [])].sort(compareReleasesByRecency),
    [releasesQuery.data]
  );

  /** True when there are more releases than the picker loads — search reaches the rest. */
  const releasesTruncated = releasesQuery.data?.hasMore === true;
  const releasesTotal = releasesQuery.data?.total ?? 0;

  const groups = useMemo(
    () => [...(groupsQuery.data?.items || [])].sort(byCreationOrder),
    [groupsQuery.data]
  );

  const loadError = releasesQuery.error || groupsQuery.error;

  const invalidateReleases = () =>
    queryClient.invalidateQueries({ queryKey: releaseKeys.releases() });

  const invalidateGroups = () =>
    queryClient.invalidateQueries({ queryKey: releaseKeys.groups() });

  const createReleaseMutation = useMutation({
    mutationFn: (release: CreateRelease) => createRelease(auth0, release),
    onSuccess: invalidateReleases,
  });

  const updateReleaseMutation = useMutation({
    mutationFn: ({ id, update }: { id: string; update: UpdateRelease }) =>
      updateRelease(auth0, id, update),
    onSuccess: invalidateReleases,
  });

  const deleteReleaseMutation = useMutation({
    mutationFn: (id: string) => deleteRelease(auth0, id),
    onSuccess: invalidateReleases,
  });


  const createGroupMutation = useMutation({
    mutationFn: (group: CreateReleaseGroup) => createReleaseGroup(auth0, group),
    onSuccess: invalidateGroups,
  });

  const updateGroupMutation = useMutation({
    mutationFn: ({ id, update }: { id: string; update: UpdateReleaseGroup }) =>
      updateReleaseGroup(auth0, id, update),
    onSuccess: invalidateGroups,
  });

  const deleteGroupMutation = useMutation({
    mutationFn: (id: string) => deleteReleaseGroup(auth0, id),
    onSuccess: async () => {
      await invalidateGroups();
      await invalidateReleases();
    },
  });

  return {
    releases,
    groups,
    releasesTruncated,
    releasesTotal,
    isLoading: releasesQuery.isLoading || groupsQuery.isLoading,
    isError: releasesQuery.isError || groupsQuery.isError,
    error: loadError,
    /**
     * Read out of the `{success, error: {code, message}}` envelope the
     * backend returns on every 400 and 404 — never assumed to be a bare
     * string.
     */
    errorMessage: loadError ? getApiErrorMessage(loadError) : null,

    createRelease: (release: CreateRelease) =>
      createReleaseMutation.mutateAsync(release),
    updateRelease: (id: string, update: UpdateRelease) =>
      updateReleaseMutation.mutateAsync({ id, update }),
    deleteRelease: (id: string) => deleteReleaseMutation.mutateAsync(id),

    createGroup: (group: CreateReleaseGroup) =>
      createGroupMutation.mutateAsync(group),
    updateGroup: (id: string, update: UpdateReleaseGroup) =>
      updateGroupMutation.mutateAsync({ id, update }),
    deleteGroup: (id: string) => deleteGroupMutation.mutateAsync(id),

    isMutating:
      createReleaseMutation.isPending ||
      updateReleaseMutation.isPending ||
      deleteReleaseMutation.isPending ||
      createGroupMutation.isPending ||
      updateGroupMutation.isPending ||
      deleteGroupMutation.isPending,
  };
};

/**
 * Writes a **product item's** releases. Its own set — an idea linked to it has
 * a separate one, written by `useIdeaReleases` below, and neither call touches
 * the other's.
 *
 * `PUT /product-items/:id/releases` is a **full replacement**: pass the whole
 * desired set, and `[]` to clear. Every set of existing ids is accepted — a
 * group is a tag, so two releases sharing one is ordinary.
 *
 * Patches the joined `productItem.releaseIds` on every ideas-list row linking
 * this item, and never invalidates: the list is paged and sorted server-side,
 * so refetching it to reflect one cell would reshuffle the page under the user.
 *
 * The patch is not cosmetic. `ReleaseCell` seeds its selection from the row, and
 * the list is served from cache on return (`refetchOnMount: false`), so a stale
 * row plus a full-replacement write deletes releases the user never removed.
 */
export const useProductItemReleases = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({
      productItemId,
      releaseIds,
    }: {
      productItemId: string;
      releaseIds: string[];
    }) => updateProductItemReleases(auth0, productItemId, releaseIds),
    onSuccess: (accepted, { productItemId, releaseIds }) => {
      ideaKeys.patchProductItemInListCache(queryClient, productItemId, {
        releaseIds: accepted?.releaseIds ?? releaseIds,
      });
    },
  });

  return {
    setReleases: (productItemId: string, releaseIds: string[]): Promise<ProductItem> =>
      mutation.mutateAsync({ productItemId, releaseIds }),
    isSaving: mutation.isPending,
    error: mutation.error,
  };
};

/**
 * Writes an **idea's own** releases through `PUT /v1/ideas/:id/releases`, which
 * returns `{_id, accountId, releaseIds}` rather than the idea. Full
 * replacement, same as the product-item call, and `[]` clears.
 *
 * The linked product item's releases are a different set and are untouched by
 * this.
 *
 * Both the idea **detail** and the ideas **list** caches are patched with the
 * response, and neither is invalidated: the list is paged and sorted
 * server-side, so refetching it to reflect one edit reshuffles the page under
 * the user, and a patch has no refetch to race.
 *
 * Both, because they feed each other. The detail query is seeded as
 * `initialData` with a 5-minute `staleTime` and `refetchOnMount: false`
 * whenever a detail cache exists (`ideaPage/useIdeaQueries.ts`), and
 * `IdeasTable.handleOnRowClick` writes the whole **list row** into
 * `ideaKeys.detail(ideaId)` on navigation. Patch only the detail and the next
 * row click overwrites it with the stale row — and because this endpoint is a
 * full replacement, the next save computed from that stale set deletes
 * releases the user never removed.
 */
export const useIdeaReleases = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({
      ideaId,
      releaseIds,
    }: {
      ideaId: string;
      releaseIds: string[];
    }) => updateIdeaReleases(auth0, ideaId, releaseIds),
    onSuccess: (accepted, { ideaId, releaseIds }) => {
      const nextIds = accepted?.releaseIds ?? releaseIds;
      queryClient.setQueryData(ideaKeys.detail(ideaId), (cached: any) =>
        cached ? { ...cached, releaseIds: nextIds } : cached
      );
      ideaKeys.patchIdeaInListCache(queryClient, ideaId, {
        releaseIds: nextIds,
      });
    },
  });

  return {
    setReleases: (
      ideaId: string,
      releaseIds: string[]
    ): Promise<IdeaReleaseAssignment> =>
      mutation.mutateAsync({ ideaId, releaseIds }),
    isSaving: mutation.isPending,
    error: mutation.error,
  };
};
